import axios from 'axios';

import { DATE_TIME_FORMAT } from 'constants/time';
import { FetchSampleCsvParams, FetchSamplesParams } from 'types/dto/sample.dto';
import Sample from 'types/models/sample';
import { SamplePointId } from 'types/models/samplePoint';
import { APIResponseParsed } from 'types/response';
import downloadFile, { prepareFilename } from 'utils/download-file';
import { getRequest } from 'utils/HttpRequest/http-request-utils';

import { parseAxiosError } from './utils/parse-axios-error';

// ==============================
// REQUESTS
// ==============================
async function fetchSamplesCsvContent(
  params: FetchSampleCsvParams,
  sensorName: string
): Promise<APIResponseParsed<'OK'>> {
  try {
    const { samplePointId, dateRange, withErrorSamples } = params;
    // Call API
    const response = await getRequest<BlobPart>(`/samplepoint/${samplePointId}/export`, {
      params: {
        startDate: dateRange[0].utc().format(),
        endDate: dateRange[1].utc().format(),
        format: 'csv',
        withErrorSamples
      }
    });

    // Prepare filename
    const [startDateFormatted, endDateFormatted] = dateRange.map(m => m.format(DATE_TIME_FORMAT.CSV_FILENAME));
    const fallbackName = `${sensorName} samples ${startDateFormatted}-${endDateFormatted}.csv`;
    const filename = prepareFilename(response, fallbackName);

    // Download file
    downloadFile(filename, response.data, 'text/csv');

    return { data: 'OK', error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetchSamples(
  samplePointId: SamplePointId,
  params: FetchSamplesParams
): Promise<APIResponseParsed<Sample[]>> {
  try {
    const response = await getRequest<Sample[]>(`/samplepoint/${samplePointId}/sample`, { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

export default {
  fetchSamples,
  fetchSamplesCsvContent
};
